'use client';

import { FC, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormTextField, FormAutocomplete } from '@/components/common/FormComponents';
import { FormLabel } from '@/components/common/FormLabel';
import { useBackendStore } from '@/stores/backendStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import {
  CollaboratorDto,
  DataPackageDto,
  CharacterizationInstrumentDto,
  SynthesisInstrumentDto,
} from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { formatCollaboratorLabel } from '@/lib/options/userUtils';
import { useAuthStore } from '@/stores/authStore';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createDataPackageSaveValidation } from '@/lib/validation/saveValidationBuilders';
import { getProblemDetail } from '@/lib/utils/apiErrors';

const selectOptions: OptionKey[] = ['synthesisInstruments', 'characterizationInstruments', 'staff'];

/** Deduplicate staff by id to avoid duplicate-key errors when names match (e.g. multiple "Read Only" entries). */
function deduplicateStaff(staff: CollaboratorDto[] | undefined): CollaboratorDto[] {
  if (!staff?.length) return [];
  const seen = new Set<string>();
  return staff.filter((s) => s.id && !seen.has(s.id) && seen.add(s.id));
}

interface AddDataPackageDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: DataPackageDto) => void;
}

interface AddDataPackageDialogInnerProps extends AddDataPackageDialogProps {
  staffOptions: CollaboratorDto[];
  synthesisInstruments: SynthesisInstrumentDto[];
  characterizationInstruments: CharacterizationInstrumentDto[];
}

const AddDataPackageDialogInner: FC<AddDataPackageDialogInnerProps> = ({
  open,
  onClose,
  onSubmit,
  staffOptions,
  synthesisInstruments,
  characterizationInstruments,
}) => {
  const backend = useBackendStore((state) => state.getBackend());
  const { showSnackbar } = useSnackbar();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [collaborators, setCollaborators] = useState<CollaboratorDto[]>([]);
  const [synthesisInstrumentSelection, setSynthesisInstrumentSelection] = useState<SynthesisInstrumentDto[]>([]);
  const [characterizationInstrumentSelection, setCharacterizationInstrumentSelection] = useState<
    CharacterizationInstrumentDto[]
  >([]);

  const [titleError, setTitleError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [collaboratorsError, setCollaboratorsError] = useState(false);

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setKeywords('');
    setCollaborators([]);
    setSynthesisInstrumentSelection([]);
    setCharacterizationInstrumentSelection([]);
    setTitleError(false);
    setDescriptionError(false);
    setCollaboratorsError(false);
    onClose();
  };

  const handleSubmit = async () => {
    const saveValidation = validateRequiredToSave(
      createDataPackageSaveValidation({
        label: title,
        description,
        collaboratorIds: collaborators.map((c) => c.id).filter((id): id is string => !!id),
      })
    );
    setTitleError(!!saveValidation.fieldErrors.label);
    setDescriptionError(!!saveValidation.fieldErrors.description);
    setCollaboratorsError(!!saveValidation.fieldErrors.collaboratorIds);

    if (!saveValidation.isValid) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    try {
      const payload = {
        label: title.trim(),
        description: description.trim(),
        keywords: keywords.trim()
          ? keywords
              .trim()
              .split(',')
              .map((k) => k.trim())
              .filter((k) => k)
          : [],
        collaboratorIds: collaborators.map((c) => c.id).filter((id) => id),
        synthesisInstrumentIds: synthesisInstrumentSelection
          .map((i) => i.id)
          .filter((id): id is string => id !== undefined),
        characterizationInstrumentIds: characterizationInstrumentSelection
          .map((i) => i.id)
          .filter((id): id is string => id !== undefined),
        status: 'pending' as const,
        isSnapshot: false,
      };

      const savedDataPackage = await backend.data.create(payload);
      log.debug('[AddDataPackageDialog] Saved data package:', savedDataPackage);

      onSubmit?.(savedDataPackage);
      showSnackbar('Data package created successfully.', 'success');
      handleClose();
    } catch (error: unknown) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to create data package.', 'error');
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Create Data Package"
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText="Create"
      cancelText="Cancel"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
        <FormTextField
          label="Title"
          required
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (titleError) setTitleError(false);
          }}
          fullWidth
          error={titleError}
          helperText={titleError ? 'Title is required' : ''}
        />

        <Box>
          <FormLabel required isEditMode={true}>
            Description
          </FormLabel>
          <RichTextEditor value={description} onChange={setDescription} />
          {descriptionError ? (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              Description is required
            </Typography>
          ) : null}
        </Box>

        <FormTextField
          label="Keywords"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          fullWidth
          placeholder="Enter keywords separated by commas"
          helperText="Separate multiple keywords with commas"
        />

        <FormAutocomplete<CollaboratorDto, true>
          multiple
          options={staffOptions}
          getOptionLabel={formatCollaboratorLabel}
          value={collaborators}
          onChange={(_, newValue) => {
            setCollaborators(Array.isArray(newValue) ? newValue : []);
            if (collaboratorsError) setCollaboratorsError(false);
          }}
          label="Collaborators"
          required
          error={collaboratorsError}
          helperText={collaboratorsError ? 'At least one collaborator is required' : ''}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {formatCollaboratorLabel(option)}
            </li>
          )}
          autoSelect
          autoHighlight
        />

        <FormAutocomplete
          multiple
          options={synthesisInstruments}
          getOptionLabel={(option) => `${option.id} - ${option.name}`}
          value={synthesisInstrumentSelection}
          onChange={(_, newValue) => setSynthesisInstrumentSelection(newValue)}
          label="Synthesis Instruments"
          autoSelect
          autoHighlight
        />

        <FormAutocomplete
          multiple
          options={characterizationInstruments}
          getOptionLabel={(option) => `${option.id} - ${option.name}`}
          value={characterizationInstrumentSelection}
          onChange={(_, newValue) => setCharacterizationInstrumentSelection(newValue)}
          label="Characterization Instruments"
          autoSelect
          autoHighlight
        />
      </Box>
    </StandardDialog>
  );
};

export const AddDataPackageDialog: FC<AddDataPackageDialogProps> = ({ open, onClose, onSubmit }) => {
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const { currentUser } = useAuthStore();

  const staffOptions = useMemo(() => deduplicateStaff(options.staff), [options.staff]);
  const synthesisInstruments = useMemo(() => options.synthesisInstruments ?? [], [options.synthesisInstruments]);
  const characterizationInstruments = useMemo(
    () => options.characterizationInstruments ?? [],
    [options.characterizationInstruments]
  );

  const dialogInitKey = `${currentUser?.id ?? 'anon'}|${currentUser?.selectedRole ?? ''}|${String(currentUser?.usingSignInAs ?? false)}`;

  if (optionsLoading) {
    return (
      <StandardDialog
        open={open}
        onClose={onClose}
        title="Create Data Package"
        maxWidth="md"
        onSubmit={() => {}}
        submitText="Create"
        cancelText="Cancel"
        submitDisabled
      >
        <Box sx={{ p: 2 }}>Loading options...</Box>
      </StandardDialog>
    );
  }

  return (
    <AddDataPackageDialogInner
      key={dialogInitKey}
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      staffOptions={staffOptions}
      synthesisInstruments={synthesisInstruments}
      characterizationInstruments={characterizationInstruments}
    />
  );
};
